import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EmailAndCodeAPI from '../helpers/EmailAndCodeAPI';
import ValidateCodeAPI from '../helpers/ValidateCodeAPI';
import checkQuizStatus from '../helpers/checkQuizStatus';
import '../App.scss';

const CodeForm: React.FC = () => {
  const { code } = useParams<{ code?: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    pfr: '',
    name: '',
    email: '',
    acceptPrivacy: false,
  });

  const [errors, setErrors] = useState({
    pfr: false,
    name: false,
    email: false,
    acceptPrivacy: false,
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [codeFromURL, setCodeFromURL] = useState<string | null>(null);
  const [isCodeValid, setIsCodeValid] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  
  // Novi state za proveru statusa kviza
  const [isQuizActive, setIsQuizActive] = useState<boolean | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  // Provera da li je kviz aktivan (na mount)
  useEffect(() => {
    checkStatus();
  }, []);

  // Validacija koda iz URL-a
  useEffect(() => {
    if (code) {
      validateURLCode(code);
    }
  }, [code]);

  const checkStatus = async () => {
    setIsCheckingStatus(true);
    const status = await checkQuizStatus();
    setIsQuizActive(status.isActive); // Uzmi samo isActive iz response objekta
    setIsCheckingStatus(false);
  };

  const validateURLCode = async (urlCode: string) => {
    setIsValidating(true);
    const isValid = await ValidateCodeAPI(urlCode);
    setIsCodeValid(isValid);
    if (isValid) {
      setCodeFromURL(urlCode.toUpperCase());
    }
    setIsValidating(false);
  };

  const validate = (cleanedData: typeof formData) => {
    let isValid = true;
    const newErrors = { pfr: false, name: false, email: false, acceptPrivacy: false };

    // Validacija koda samo ako NIJE došao iz URL-a
    if (!codeFromURL) {
      // Novi format: tačno 6 alfanumeričkih karaktera (A-Z, 0-9), bez crtica
      // Primer: A1B2C3 ili a1b2c3 (backend konvertuje u velika slova)
      const pfrPattern = /^[A-Za-z0-9]{6}$/;
      if (!cleanedData.pfr || !pfrPattern.test(cleanedData.pfr)) {
        isValid = false;
        newErrors.pfr = true;
      }
    }

    // Pattern prihvata latinična i ćirilična slova + međunarodne karaktere (ć, č, š, đ, ž, itd.)
    const namePattern = /^[a-zA-ZčćžšđČĆŽŠĐàáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿĀāĂăĄąĆćĈĉĊċČčĎďĐđĒēĔĕĖėĘęĚěĜĝĞğĠġĢģĤĥĦħĨĩĪīĬĭĮįİıĲĳĴĵĶķĸĹĺĻļĽľĿŀŁłŃńŅņŇňŉŊŋŌōŎŏŐőŒœŔŕŖŗŘřŚśŜŝŞşŠšŢţŤťŦŧŨũŪūŬŭŮůŰűŲųŴŵŶŷŸŹźŻżŽžſ\s]+$/;
    if (!cleanedData.name || !namePattern.test(cleanedData.name)) {
      isValid = false;
      newErrors.name = true;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!cleanedData.email || !emailPattern.test(cleanedData.email)) {
      isValid = false;
      newErrors.email = true;
    }

    if (!cleanedData.acceptPrivacy) {
      isValid = false;
      newErrors.acceptPrivacy = true;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setErrors({ ...errors, [name]: false });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Koristi kod iz URL-a ako postoji, inače iz forme
    const finalCode = codeFromURL || formData.pfr.trim().toUpperCase();

    // Čišćenje input podataka pre validacije i slanja
    const cleanedData = {
      pfr: finalCode,
      name: formData.name.trim().replace(/\s+/g, ' '), // uklanja višestruke razmake
      email: formData.email.trim().toLowerCase(),
      acceptPrivacy: formData.acceptPrivacy,
    };

    if (!validate(cleanedData)) {
      setErrorMessage('Molimo vas da proverite podatke koje ste uneli.');
      return;
    }

    const token = '6LeSRygnAAAAAG2JDpGldjusIALOAVRuBms1hkhG';
    try {
      const statusCode = await EmailAndCodeAPI(
        cleanedData.email,
        cleanedData.pfr,
        cleanedData.name,
        token
      );

      if (statusCode === 201) {
        navigate('/kviz', {
          state: {
            email: cleanedData.email,
            code: cleanedData.pfr,
            name: cleanedData.name,
          },
        });
      } else {
        setErrorMessage('Registracija nije uspela. Molimo vas da proverite svoje podatke ili kontaktirate podršku.');
      }
    } catch (error) {
      setErrorMessage('Došlo je do greške. Molimo pokušajte ponovo.');
      console.error('API Error:', error);
    }
  };

  // Ako se proverava status kviza - prikaži loader
  if (isCheckingStatus) {
    return (
      <div className="form-container">
        <div className="validation-message">
          <h2>Učitavanje...</h2>
          <p>Molimo vas sačekajte.</p>
        </div>
      </div>
    );
  }

  // Ako kviz NIJE aktivan - prikaži poruku
  if (isQuizActive === false) {
    return (
      <div className="form-container">
        <div className="quiz-inactive-message">
          <h2>⏳ Kviz još nije aktivan</h2>
          <p>Molimo vas sačekajte da organizator pokrene kviz.</p>
          <p>Pokušajte ponovo za nekoliko trenutaka.</p>
        </div>
      </div>
    );
  }

  // Ako se validira kod - prikaži loader
  if (isValidating) {
    return (
      <div className="form-container">
        <div className="validation-message">
          <h2>Validacija koda...</h2>
          <p>Molimo vas sačekajte.</p>
        </div>
      </div>
    );
  }

  // Ako kod nije validan - prikaži error
  if (code && isCodeValid === false) {
    return (
      <div className="form-container">
        <div className="error-message">
          <h2>❌ URL nije ispravan</h2>
          <p>Kod nije validan ili je već iskorišćen.</p>
          <p>Molimo vas proverite QR kod ili kontaktirajte podršku.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="form" noValidate>
        {/* Prikaži polje za kod SAMO ako nije došlo sa URL-a */}
        {!codeFromURL && (
          <>
            <label htmlFor="pfr">UNESITE KOD</label>
            <input
              type="text"
              id="pfr"
              name="pfr"
              value={formData.pfr}
              onChange={handleChange}
            />
            {errors.pfr && <p className="error">Nevažeći QR KOD</p>}
          </>
        )}

        <label htmlFor="name">IME I PREZIME</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
        />
        {errors.name && <p className="error">Unesite svoje ime i prezime</p>}

        <label htmlFor="email">MAIL</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
        />
        {errors.email && <p className="error">Molimo vas da unesete validnu adresu e-pošte</p>}

        <div className="privacy">
          <input
            type="checkbox"
            id="acceptPrivacy"
            name="acceptPrivacy"
            checked={formData.acceptPrivacy}
            onChange={handleChange}
          />
          <label htmlFor="acceptPrivacy">
            <a
              href="/Заштита_података_о_личности.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Prihvatam politiku privatnosti
            </a>
          </label>
          {errors.acceptPrivacy && (
            <p className="error">Morate prihvatiti politiku privatnosti</p>
          )}
        </div>

        {errorMessage && <p className="error">{errorMessage}</p>}

        <button type="submit" className="submit-button">POKRENI KVIZ</button>
      </form>
    </div>
  );
};

export default CodeForm;
